(ns practitest.frontend.find-and-replace-modal.events
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [day8.re-frame.http-fx]
    [practitest.frontend.common-entities :as common-entities]
    [practitest.frontend.common-entities.metadata :as metadata :refer [get-entity-name]]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

(def default-state
  {:fields          []
   :find-val        ""
   :replace-val     ""
   :selected-fields #{}
   ;; :idle - no results yet (or last query matched nothing); :found - last Find matched >=1.
   :stage           :idle
   :message         nil
   :match-count     nil})

(defn- track-action! [action extra-props]
  (mixpanel/track "Find and Replace" (merge {"Action Name" action} extra-props)))

(defn- entities-ids [selection current-view]
  (common-entities/prepare-full-selection-ids selection current-view))

;; {:cv id} or {:auto_filter {...}} - resolves the same view the entities-ids sentinel refers to.
(defn- view-params [current-view]
  (common-entities/build-filter current-view))

(defn- selected-field-labels [fields selected-fields]
  (->> fields
       (filter #(contains? selected-fields (first %)))
       (map second)))

(re-frame/reg-event-fx
  ::open
  (fn [{:keys [db]} [_ {:keys [entity selection current-view state-key optional-params]}]]
    (track-action! "Modal Opened" {"Entity" (mixpanel/entity-type->mixpanel entity)})
    (let [grid-filter (:filter optional-params)
          route-name (get-in metadata/entities-metadata [entity :urls :find-and-replace-fields])]
      {:db (assoc db :find-and-replace-modal
                  (assoc default-state
                         :open? true
                         :entity entity
                         :selection selection
                         :current-view current-view
                         :state-key state-key))
       :http-xhrio
       {:method          :get
        :uri             (broutes/get-url route-name {:p_id (subs/get-current-project-id db)})
        :format          :url
        :vec-strategy    :rails
        :params          (merge (cond-> {:ids (entities-ids selection current-view)}
                                   (= selection :_all) (assoc :filter grid-filter))
                                 (view-params current-view))
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::fields-loaded]
        :on-failure      [::fields-load-failed]}})))

(re-frame/reg-event-db
  ::fields-loaded
  (fn [db [_ response]]
    (assoc-in db [:find-and-replace-modal :fields] (:fields response))))

(re-frame/reg-event-fx
  ::fields-load-failed
  (fn [{:keys [db]} [_ _error]]
    {:db (assoc-in db [:find-and-replace-modal :open?] false)
     :dispatch [::notifications/error2
                (get-translation "generic.error.network-error" "Error fetching data from server")
                {:auto-close true}]}))

(re-frame/reg-event-fx
  ::close
  (fn [{:keys [db]} [_]]
    (track-action! "Modal Closed" {})
    {:db (assoc-in db [:find-and-replace-modal :open?] false)}))

(re-frame/reg-event-fx
  ::cancel
  (fn [{:keys [db]} [_]]
    (track-action! "Modal Cancelled" {})
    {:db (assoc-in db [:find-and-replace-modal :open?] false)}))

;; Editing the query or the field selection invalidates whatever the last Find found.
(defn- reset-results [db]
  (-> db
      (assoc-in [:find-and-replace-modal :stage] :idle)
      (assoc-in [:find-and-replace-modal :message] nil)
      (assoc-in [:find-and-replace-modal :match-count] nil)))

(re-frame/reg-event-db
  ::set-find-val
  (fn [db [_ v]]
    (-> db
        (assoc-in [:find-and-replace-modal :find-val] v)
        reset-results)))

(re-frame/reg-event-db
  ::set-replace-val
  (fn [db [_ v]]
    (assoc-in db [:find-and-replace-modal :replace-val] v)))

(re-frame/reg-event-db
  ::set-selected-fields
  (fn [db [_ fields-set]]
    (-> db
        (assoc-in [:find-and-replace-modal :selected-fields] fields-set)
        reset-results)))

(re-frame/reg-event-fx
  ::find
  (fn [{:keys [db]} [_]]
    (let [{:keys [entity selection current-view find-val selected-fields]} (:find-and-replace-modal db)
          route-name (get-in metadata/entities-metadata [entity :urls :find-replace-check])]
      (track-action! "Find Clicked" {"find_text" find-val "fields" (vec selected-fields)})
      {:db (assoc-in db [:find-and-replace-modal :finding?] true)
       :http-xhrio
       {:method          :post
        :uri             (broutes/get-url route-name {:p_id (subs/get-current-project-id db)})
        :params          (merge {:find-value       find-val
                                  :fields-to-change (vec selected-fields)
                                  :entities-ids     (entities-ids selection current-view)}
                                 (view-params current-view))
        :format          (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::find-succeeded]
        :on-failure      [::find-failed]}})))

(re-frame/reg-event-db
  ::find-succeeded
  (fn [db [_ {:keys [e_count msg]}]]
    (-> db
        (assoc-in [:find-and-replace-modal :finding?] false)
        (assoc-in [:find-and-replace-modal :message] msg)
        (assoc-in [:find-and-replace-modal :match-count] e_count)
        (assoc-in [:find-and-replace-modal :stage] (if (pos? e_count) :found :idle)))))

(re-frame/reg-event-fx
  ::find-failed
  (fn [{:keys [db]} [_ _error]]
    {:db (assoc-in db [:find-and-replace-modal :finding?] false)
     :dispatch [::notifications/error2
                (get-translation "generic.error.network-error" "Error fetching data from server")
                {:auto-close true}]}))

;; Re-fetches the grid's current page instead of the old full-page redirect on completion.
(defn- refresh-grid-fx [db]
  (let [entity (get-in db [:find-and-replace-modal :entity])
        state-key (get-in db [:find-and-replace-modal :state-key])
        table-id (get-in metadata/entities-metadata [entity :table-id])
        storage-key (str (subs/get-current-project-id db) "-" table-id)
        per-page (or (grid-storage/load-per-page storage-key) 10)
        current-page (or (grid-storage/load-current-page storage-key) 1)]
    [:practitest.frontend.common-entities.events/fetch-entity-list entity state-key
     {:per_page per-page :page current-page}]))

(re-frame/reg-event-fx
  ::replace-all
  (fn [{:keys [db]} [_]]
    (let [{:keys [entity selection current-view find-val replace-val selected-fields
                  match-count fields]} (:find-and-replace-modal db)
          route-name (get-in metadata/entities-metadata [entity :urls :find-and-replace-apply])
          field-label (str/join ", " (selected-field-labels fields selected-fields))]
      (track-action! "Replace All Clicked" {"match_count" match-count "field" field-label})
      {:db (assoc-in db [:find-and-replace-modal :submitting?] true)
       :http-xhrio
       {:method          :post
        :uri             (broutes/get-url route-name {:p_id (subs/get-current-project-id db)})
        :params          (merge {:find-value       find-val
                                  :replace-value    replace-val
                                  :fields-to-change (vec selected-fields)
                                  :entities-ids     (entities-ids selection current-view)}
                                 (view-params current-view))
        :format          (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::replace-all-succeeded]
        :on-failure      [::replace-all-failed]}})))

(re-frame/reg-event-fx
  ::replace-all-succeeded
  (fn [{:keys [db]} [_ _response]]
    (let [{:keys [entity match-count]} (:find-and-replace-modal db)
          entity-label (get-entity-name entity :plural-name)]
      {:db (-> db
               (assoc-in [:find-and-replace-modal :submitting?] false)
               (assoc-in [:find-and-replace-modal :open?] false))
       :dispatch-n [(refresh-grid-fx db)
                    [::notifications/success2
                     (format-translation
                       (get-translation "batch-modal.submit.partial-success"
                                        "%{count} %{entity} were updated successfully.")
                       {:count match-count :entity entity-label})
                     {:auto-close true}]]})))

(re-frame/reg-event-fx
  ::replace-all-failed
  (fn [{:keys [db]} [_ _error]]
    {:db (assoc-in db [:find-and-replace-modal :submitting?] false)
     :dispatch [::notifications/error2
                (get-translation "generic.error.network-error" "Error fetching data from server")
                {:auto-close true}]}))
